import { useRef, useState } from 'react'
import type { PhotoStore } from '@application/ports.ts'
import type { FamilyState, Task } from '@domain/state.ts'
import { taskDayOf } from '@domain/state.ts'
import type { PhotoId, StepId } from '@domain/ids.ts'
import { es } from '../strings/es.ts'
import { Meter } from './Meter.tsx'
import { Pill } from './Pill.tsx'
import { usePhoto } from './usePhoto.ts'

export function TaskScreen({
  task,
  state,
  day,
  photos,
  accent,
  coinWord,
  warnOnChange,
  oneStepAtATime,
  onCompleteStep,
  onUncompleteStep,
  onMarkDone,
  onBack,
}: {
  task: Task
  state: FamilyState
  day: string
  photos: PhotoStore | null
  accent: string
  coinWord: string
  warnOnChange: boolean
  /** Enseña solo el paso que toca. Misma adaptación que la del día. */
  oneStepAtATime: boolean
  onCompleteStep: (stepId: StepId) => void
  onUncompleteStep: (stepId: StepId) => void
  onMarkDone: (photoId?: PhotoId) => Promise<string | null>
  onBack: () => void
}) {
  const today = taskDayOf(state, day as never, task.memberId, task.id)
  const [photoId, setPhotoId] = useState<PhotoId | undefined>(today.photoId)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const fileInput = useRef<HTMLInputElement>(null)

  const referenceUrl = usePhoto(photos, task.referencePhotoId)
  const takenUrl = usePhoto(photos, photoId)

  const done = task.steps.filter((s) => today.steps.has(s.id)).length
  const [verTodos, setVerTodos] = useState(false)
  const pendientes = task.steps.filter((s) => !today.steps.has(s.id))
  const unoSolo = oneStepAtATime && !verTodos && pendientes.length > 0
  const visibles = unoSolo ? pendientes.slice(0, 1) : task.steps
  const marked = today.status === 'marked'
  const validated = today.status === 'validated'
  const reward = task.rewards[task.rung]

  const capture = async (file: File): Promise<void> => {
    if (!photos) return
    const id = await photos.put(file, 'result')
    setPhotoId(id)
  }

  const markDone = async (): Promise<void> => {
    setBusy(true)
    try {
      setError(await onMarkDone(photoId))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="mx-auto max-w-2xl px-5 pt-5 pb-12">
      <button type="button" onClick={onBack} className="text-muted mb-5 text-sm font-bold">
        {es.today.back}
      </button>

      <h1 className="mb-1 flex items-center gap-3 text-3xl font-extrabold tracking-tight">
        <span aria-hidden="true">{task.emoji ?? '⭐'}</span>
        {task.name}
      </h1>
      <p className="text-muted mt-0 mb-5 text-sm font-bold">
        {es.task.pays(reward.coins, reward.xp, coinWord)} · {es.task.rung(task.rung)}
      </p>

      {warnOnChange && task.changedOn === day ? (
        <p role="status" className="bg-gold-soft text-gold m-0 mb-4 rounded-lg p-3 font-bold">
          {es.today.changedToday}
        </p>
      ) : null}

      {validated ? <Pill tone="done">{es.today.validated}</Pill> : null}
      {marked ? <Pill tone="wait">{es.task.marked}</Pill> : null}
      {today.returnedNote !== undefined ? <Pill tone="back">{today.returnedNote}</Pill> : null}

      {referenceUrl ? (
        <figure className="my-6">
          <img src={referenceUrl} alt={es.task.reference} className="w-full rounded-xl" />
          <figcaption className="text-muted mt-2 text-sm">{es.task.reference}</figcaption>
        </figure>
      ) : null}

      {task.steps.length > 0 ? (
        <section className="my-6">
          <h2 className="text-muted mb-2 text-xs font-bold tracking-[0.1em] uppercase">
            {es.task.steps}
          </h2>
          <p className="text-muted mt-0 mb-2 text-sm">
            {es.today.stepsDone(done, task.steps.length)}
          </p>
          <Meter done={done} total={task.steps.length} accent={accent} />
          <ul className="mt-4 grid list-none gap-3 p-0">
            {/*
              Una lista de cinco pasos son cinco decisiones antes del primer
              movimiento. Para quien se agobia en el arranque, enseñar solo el
              que toca es la diferencia entre empezar y no empezar — y la lista
              entera sigue estando a un toque de «verlo todo», porque una
              pantalla de la que no se puede salir es su propia presión.
            */}
            {visibles.map((step) => {
              const checked = today.steps.has(step.id)
              return (
                <li key={step.id} className="m-0">
                  <button
                    type="button"
                    disabled={validated}
                    aria-pressed={checked}
                    title={checked ? es.task.undoStep : undefined}
                    onClick={() => {
                      // Ticking a step answers whatever the button just said,
                      // so the old message goes away with it.
                      setError(null)
                      if (checked) onUncompleteStep(step.id)
                      else onCompleteStep(step.id)
                    }}
                    className={`border-rule flex w-full items-center gap-4 rounded-xl border-2 p-4 text-left text-lg font-bold ${
                      checked ? 'bg-mint-soft text-mint border-mint' : 'bg-surface'
                    }`}
                  >
                    <span
                      aria-hidden="true"
                      className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border-2 text-xl ${
                        checked ? 'border-mint' : 'border-rule'
                      }`}
                    >
                      {checked ? '✓' : ''}
                    </span>
                    {step.text}
                  </button>
                </li>
              )
            })}
          </ul>
          {oneStepAtATime && pendientes.length > 1 ? (
            <button
              type="button"
              onClick={() => {
                setVerTodos(!verTodos)
              }}
              className="text-muted mt-3 text-sm font-bold underline underline-offset-4"
            >
              {verTodos ? es.task.onlyNext : es.task.allSteps(task.steps.length)}
            </button>
          ) : null}
        </section>
      ) : null}

      {task.requiresPhoto && !validated ? (
        <section className="my-6">
          {takenUrl ? (
            <img src={takenUrl} alt={es.task.photoTaken} className="mb-3 w-full rounded-xl" />
          ) : null}
          <button
            type="button"
            className="border-rule w-full rounded-xl border-2 px-6 py-4 text-lg font-bold"
            onClick={() => fileInput.current?.click()}
          >
            📷 {takenUrl ? es.task.retakePhoto : es.task.takePhoto}
          </button>
          <input
            ref={fileInput}
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            aria-label={es.task.takePhoto}
            onChange={(e) => {
              const file = e.target.files?.[0]
              if (file) void capture(file)
              e.target.value = ''
            }}
          />
        </section>
      ) : null}

      {!marked && !validated ? (
        <button
          type="button"
          disabled={busy}
          onClick={() => {
            void markDone()
          }}
          className="mt-4 w-full rounded-xl px-6 py-5 text-xl font-extrabold text-white disabled:opacity-40"
          style={{ background: accent }}
        >
          {es.task.markDone}
        </button>
      ) : null}

      {error ? (
        <p role="alert" className="text-coral mt-4 mb-0 font-bold">
          {error}
        </p>
      ) : null}
    </div>
  )
}
